import React, { useState } from 'react'
import { useApp } from '../hooks/useApp'
import { useAuth } from '../hooks/useAuth'
import LoadingSpinner from './LoadingSpinner'
import { normalizeWebsiteUrl, prefillUrlFromSearch } from '../utils/url'

function URLInput() {
  const [singleUrl, setSingleUrl] = useState(() =>
    prefillUrlFromSearch(typeof window === 'undefined' ? '' : window.location.search)
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const [showInfo, setShowInfo] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [creditModalData, setCreditModalData] = useState({ required: 1, current: 0 })

  const { processURL } = useApp()
  const { user, refreshUserProfile } = useAuth()

  const handleInsufficientCredits = (errorResponse) => {
    // Parse the error response to extract credit information
    // Expected format: { success: false, error: "Feature access denied", code: "FEATURE_ACCESS_DENIED", reason: "Insufficient credits - Purchase credits to continue", feature: "basic_analysis", credits_required: 1, credits_available: 0 }
    
    let currentCredits = user?.profile?.credits || user?.credits || 0
    let requiredCredits = 1 // Default to 1 credit for basic analysis
    
    // Try to parse as JSON if it's a string
    if (typeof errorResponse === 'string') {
      try {
        const parsed = JSON.parse(errorResponse)
        if (parsed.credits_available !== undefined) currentCredits = parsed.credits_available
        if (parsed.credits_required !== undefined) requiredCredits = parsed.credits_required
      } catch (e) {
        // If parsing fails, use defaults
      }
    } else if (typeof errorResponse === 'object') {
      // If it's already an object, extract the values
      if (errorResponse.credits_available !== undefined) currentCredits = errorResponse.credits_available
      if (errorResponse.credits_required !== undefined) requiredCredits = errorResponse.credits_required
    }
    
    setCreditModalData({
      required: requiredCredits,
      current: currentCredits
    })
    setShowCreditModal(true)
  }

  const isInsufficientCreditsError = (errorMessage) => {
    if (!errorMessage) return false
    
    // Check both the error message and if it's an object with error property
    const messageToCheck = typeof errorMessage === 'string' ? errorMessage : errorMessage.error || errorMessage.reason || ''
    
    return messageToCheck.includes('Insufficient credits') ||
           messageToCheck.includes('FEATURE_ACCESS_DENIED') ||
           messageToCheck.includes('Purchase credits to continue') ||
           messageToCheck.includes('Feature access denied')
  }

  const handleSingleSubmit = async (e) => {
    e.preventDefault()
    console.log('[URLInput] Form submitted with URL:', singleUrl)
    setError('')

    const normalized = normalizeWebsiteUrl(singleUrl)
    if (!normalized.ok) {
      setError(normalized.error)
      return
    }

    setSingleUrl(normalized.url)
    console.log('[URLInput] URL validation passed, starting processing')
    setIsProcessing(true)

    try {
      console.log('[URLInput] Calling processURL with:', normalized.url)
      const result = await processURL(normalized.url)
      console.log('[URLInput] processURL result:', result)
      
      if (result.success) {
        setSingleUrl('')
        // Success handled by global state
      } else {
        const errorMessage = result.error || 'Processing failed'
        console.log('[URLInput] Checking if error is insufficient credits:', errorMessage)
        if (isInsufficientCreditsError(errorMessage) || isInsufficientCreditsError(result)) {
          console.log('[URLInput] Handling insufficient credits error')
          handleInsufficientCredits(result) // Pass the full result object
        } else {
          setError(errorMessage)
        }
      }
    } catch (err) {
      console.error('[URLInput] processURL error:', err)
      const errorMessage = err.message || 'Network error. Please try again.'
      if (isInsufficientCreditsError(errorMessage)) {
        handleInsufficientCredits(errorMessage)
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <section>
      <form onSubmit={handleSingleSubmit} noValidate className="flex items-center gap-2">
        <label htmlFor="single-url" className="sr-only">Website URL</label>
        <div className="relative min-w-0 flex-1">
          <input
            type="text"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            id="single-url"
            className="h-12 w-full rounded-full border border-border bg-card pl-5 pr-12 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter a website"
            value={singleUrl}
            onChange={(e) => setSingleUrl(e.target.value)}
            onBlur={() => {
              const normalized = normalizeWebsiteUrl(singleUrl)
              if (normalized.ok) setSingleUrl(normalized.url)
            }}
            disabled={isProcessing}
          />
          <button
            type="button"
            aria-expanded={showInfo}
            aria-label="About this report"
            onClick={() => setShowInfo((open) => !open)}
            className="absolute inset-y-1 right-1 flex w-10 items-center justify-center rounded-full text-sm font-medium text-muted-foreground hover:bg-gray-100 hover:text-foreground"
          >
            i
          </button>
        </div>
        <button
          type="submit"
          disabled={isProcessing}
          className="h-12 shrink-0 rounded-full bg-blue-600 px-5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <LoadingSpinner size="sm" className="mr-2" />
              Working
            </span>
          ) : (
            'Generate'
          )}
        </button>
      </form>
      {showInfo && (
        <p className="mt-3 px-2 text-sm text-muted-foreground">
          Paste one site. You get about 100 SEO and GEO actions for it. One credit covers that site.
        </p>
      )}
      {error && (
        <p className="mt-3 px-2 text-sm text-red-600 whitespace-pre-line">{error}</p>
      )}
    </section>
  );
}

export default URLInput